import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ThemeProvider } from "@aws-amplify/ui-react";
import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";

// Import Amplify configuration
import "./lib/amplify";

// Import pages
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import LeagueOverview from "./pages/LeagueOverview";
import Standings from "./pages/Standings";
import Events from "./pages/Events";
import EventDetail from "./pages/EventDetail";
import CommanderPods from "./pages/CommanderPods";
import UserEventRegistration from "./pages/EventRegistration";
import ProfileSetup from "./pages/ProfileSetup";
import Profile from "./pages/Profile";
import LeaguePlayers from "./pages/LeaguePlayers";
import PlayerHistory from "./pages/PlayerHistory";
import PublicStandings from "./pages/PublicStandings";
import AdminHome from "./pages/Admin/AdminHome";
import EventResults from "./pages/Admin/EventResults";
import EventPlacements from "./pages/Admin/EventPlacements";
import PromoteUser from "./pages/Admin/PromoteUser";
import LeagueManagement from "./pages/Admin/LeagueManagement";
import CreateLeague from "./pages/Admin/CreateLeague";
import CreateUser from "./pages/Admin/CreateUser";
import TestData from "./pages/Admin/TestData";
import LeagueLeaderboard from "./pages/Admin/LeagueLeaderboard";
import DataAudit from "./pages/Admin/DataAudit";
import GraphQLTest from "./pages/Admin/GraphQLTest";
import LeagueDetail from "./pages/Admin/LeagueDetail";
import LeaguePointAdjustments from "./pages/Admin/LeaguePointAdjustments";
import EditLeague from "./pages/Admin/EditLeague";
import CreateEvent from "./pages/Admin/CreateEvent";
import AdminEventDetail from "./pages/Admin/EventDetail";
import EditEvent from "./pages/Admin/EditEvent";
import EventRegistration from "./pages/Admin/EventRegistration";
import CommanderRounds from "./pages/Admin/CommanderRounds";
import EventStandings from "./pages/Admin/EventStandings";
import EventStandingsImport from "./pages/Admin/EventStandingsImport";
import EventStandingsAssignment from "./pages/Admin/EventStandingsAssignment";
import TopCutResults from "./pages/Admin/TopCutResults";
import PlayerConstructMapping from "./pages/Admin/PlayerConstructMapping";
import ResetPoints from "./pages/Admin/ResetPoints";

// Import components
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import OAuthCallback from "./components/OAuthCallback";
import RealtimeQuerySync from "./components/RealtimeQuerySync";

// Keep every screen in sync after writes, navigation, reconnects, and
// changes made in another browser session.
let queryClient: QueryClient;

const mutationCache = new MutationCache({
  onSuccess: async () => {
    await queryClient.invalidateQueries();
  },
});

queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // Safety net for a dropped realtime connection.
      refetchInterval: 60 * 1000,
      refetchIntervalInBackground: false,
    },
  },
});

function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <RealtimeQuerySync />
        <Router>
          <Authenticator.Provider>
            <div className="min-h-screen bg-background">
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Home />} />
                <Route path="/standings" element={<PublicStandings />} />
                <Route path="/oauth/callback" element={<OAuthCallback />} />
                <Route
                  path="/profile-setup"
                  element={
                    <ProtectedRoute skipProfileCheck={true}>
                      <ProfileSetup />
                    </ProtectedRoute>
                  }
                />

                {/* Protected routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Dashboard />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/league/:leagueId"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <LeagueOverview />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/league/:leagueId/standings"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Standings />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/league/:leagueId/standings/history"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Standings view="past" />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/league/:leagueId/events"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Events />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Profile />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/players"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <LeaguePlayers />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/players/history"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <PlayerHistory />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/players/:playerConstructId/history"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <PlayerHistory />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/event/:eventId"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <EventDetail />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/event/:eventId/register"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <UserEventRegistration />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/event/:eventId/commander-pods"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CommanderPods />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                {/* Admin routes */}
                <Route
                  path="/admin"
                  element={
                    <AdminRoute>
                      <Layout>
                        <AdminHome />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/event/:eventId/results"
                  element={
                    <AdminRoute>
                      <Layout>
                        <EventResults />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/event/:eventId/placements"
                  element={
                    <AdminRoute>
                      <Layout>
                        <EventPlacements />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/promote"
                  element={
                    <Layout>
                      <PromoteUser />
                    </Layout>
                  }
                />

                <Route
                  path="/admin/leagues"
                  element={
                    <AdminRoute>
                      <Layout>
                        <LeagueManagement />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/leagues/create"
                  element={
                    <AdminRoute>
                      <Layout>
                        <CreateLeague />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/leagues/:leagueId"
                  element={
                    <AdminRoute>
                      <Layout>
                        <LeagueDetail />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/leagues/:leagueId/edit"
                  element={
                    <AdminRoute>
                      <Layout>
                        <EditLeague />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/leagues/:leagueId/point-adjustments"
                  element={
                    <AdminRoute>
                      <Layout>
                        <LeaguePointAdjustments />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/events/create"
                  element={
                    <AdminRoute>
                      <Layout>
                        <CreateEvent />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/events/:eventId"
                  element={
                    <AdminRoute>
                      <Layout>
                        <AdminEventDetail />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/events/:eventId/edit"
                  element={
                    <AdminRoute>
                      <Layout>
                        <EditEvent />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/events/:eventId/registration"
                  element={
                    <AdminRoute>
                      <Layout>
                        <EventRegistration />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/events/:eventId/commander-rounds"
                  element={
                    <AdminRoute>
                      <Layout>
                        <CommanderRounds />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/events/:eventId/standings"
                  element={
                    <AdminRoute>
                      <Layout>
                        <EventStandings />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/events/:eventId/standings/import"
                  element={
                    <AdminRoute>
                      <Layout>
                        <EventStandingsImport />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/events/:eventId/results/import"
                  element={
                    <AdminRoute>
                      <Layout>
                        <EventResults />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/events/:eventId/standings/assignment"
                  element={
                    <AdminRoute>
                      <Layout>
                        <EventStandingsAssignment />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/events/:eventId/topcut"
                  element={
                    <AdminRoute>
                      <Layout>
                        <TopCutResults />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/create-user"
                  element={
                    <Layout>
                      <CreateUser />
                    </Layout>
                  }
                />

                <Route
                  path="/admin/test-data"
                  element={
                    <Layout>
                      <TestData />
                    </Layout>
                  }
                />

                <Route
                  path="/admin/leaderboard"
                  element={
                    <Layout>
                      <LeagueLeaderboard />
                    </Layout>
                  }
                />

                <Route
                  path="/admin/data-audit"
                  element={
                    <Layout>
                      <DataAudit />
                    </Layout>
                  }
                />

                <Route
                  path="/admin/graphql-test"
                  element={
                    <Layout>
                      <GraphQLTest />
                    </Layout>
                  }
                />

                <Route
                  path="/admin/player-mapping"
                  element={
                    <AdminRoute>
                      <Layout>
                        <PlayerConstructMapping />
                      </Layout>
                    </AdminRoute>
                  }
                />

                <Route
                  path="/admin/reset-points"
                  element={
                    <AdminRoute>
                      <Layout>
                        <ResetPoints />
                      </Layout>
                    </AdminRoute>
                  }
                />

                {/* Fallback route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </Authenticator.Provider>
        </Router>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
